import Fuse from "fuse.js";

import { formatUsDateToYyyyMmDd } from "@/src/lib/utils/date-and-phone-utils";
import type { Patient } from "@/src/models/patient";

export function patientsSearch(patients: Patient[], query: Partial<Patient> | string | null | undefined): Patient[] {
  if (!patients || patients.length === 0 || query === null || query === undefined) {
    return [];
  }

  if (typeof query === "string") {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return [];
    }

    const dateRegex = /(\d{1,2}\/\d{1,2}\/\d{4})|(\d{4}-\d{2}-\d{2})/;
    const dateMatch = trimmedQuery.match(dateRegex);
    let dateQuery = null;
    let nameQuery = trimmedQuery;

    if (dateMatch) {
      dateQuery = formatUsDateToYyyyMmDd(dateMatch[0]);
      nameQuery = trimmedQuery.replace(dateRegex, "").trim();
    }

    const searchablePatients = dateQuery ? patients.filter((patient) => patient.dateOfBirth === dateQuery) : patients;

    if (searchablePatients.length === 0) {
      return [];
    }

    if (!nameQuery && dateQuery) {
      return searchablePatients.slice(0, 20);
    }

    const exactMatches = searchablePatients.filter((patient) => {
      const givenName = patient.givenName?.toLowerCase().trim() || "";
      const familyName = patient.familyName?.toLowerCase().trim() || "";

      return `${givenName} ${familyName}` === nameQuery.toLowerCase();
    });

    if (exactMatches.length > 0) {
      return exactMatches.slice(0, 20);
    }

    const fuse = new Fuse(searchablePatients, {
      keys: ["givenName", "middleName", "familyName", "displayId", "id", "dateOfBirth"],
      threshold: 0.6,
      ignoreLocation: true,
      isCaseSensitive: false,
    });

    return fuse.search(nameQuery, { limit: 20 }).map((result) => result.item);
  }

  const givenName = query.givenName?.trim().toLowerCase() || "";
  const familyName = query.familyName?.trim().toLowerCase() || "";
  const dateOfBirth = formatUsDateToYyyyMmDd(query.dateOfBirth ?? null);

  if (!givenName) {
    return [];
  }

  const searchablePatients = dateOfBirth ? patients.filter((patient) => patient.dateOfBirth === dateOfBirth) : patients;

  if (searchablePatients.length === 0) {
    return [];
  }

  const exactMatch = searchablePatients.find(
    (patient) =>
      patient.givenName?.toLowerCase() === givenName &&
      (!familyName || patient.familyName?.toLowerCase() === familyName)
  );

  return exactMatch ? [exactMatch] : [];
}
